import { useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

import { Z_PAGE, useToolTokens, useElementSize } from './toolStyles';

// On a phone a tool is not a window but a PAGE laid over the section the person is in. The section is never unmounted
// or navigated away from, so closing the page puts them back exactly where they were - the half-filled form, the list
// scrolled to where it was. (FloatingTools also wires the phone's Back button to close it.)
export default function MobileToolPage({ name, title, icon, onClose, escapeCloses = true, children }) {
  const T = useToolTokens();
  const { t } = useTranslation();
  const bodyRef = useRef(null);
  const size = useElementSize(bodyRef);

  return (
    <Box
      role="dialog"
      aria-modal="true"
      aria-label={title}
      data-xms-tool={name}
      data-xms-tool-page="true"
      onKeyDown={(e) => { if (escapeCloses && e.key === 'Escape') { e.stopPropagation(); onClose(); } }}
      sx={{
        position: 'fixed', inset: 0, height: '100dvh', zIndex: Z_PAGE,
        display: 'flex', flexDirection: 'column', bgcolor: T.BG, color: T.TEXT,
        paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)',
        overscrollBehavior: 'contain', touchAction: 'manipulation',
      }}
    >
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1, height: 52, px: 1.5, flexShrink: 0,
        bgcolor: T.HEAD, borderBottom: `1px solid ${T.BD}`,
      }}>
        <Box sx={{ display: 'flex', color: T.TEXT2, '& svg': { fontSize: 20 } }}>{icon}</Box>
        <Typography noWrap sx={{ flexGrow: 1, fontSize: '0.95rem', fontWeight: 700 }}>{title}</Typography>
        <IconButton aria-label={t('common.close')} onClick={onClose} data-tool-close
          sx={{ width: 44, height: 44, color: T.TEXT2, '&:hover': { color: T.TEXT, bgcolor: T.CTRL_HOVER } }}>
          <CloseIcon sx={{ fontSize: 22 }} />
        </IconButton>
      </Box>
      <Box ref={bodyRef} sx={{ flexGrow: 1, minHeight: 0, overflow: 'hidden', position: 'relative' }}>
        {size.w > 0 && children(size)}
      </Box>
    </Box>
  );
}
